/* Copyright (c) 2020-present CloudIO Inc */

import { DataRecord, DBRow, IoAudit } from '@cloudio-saas/datasource-types';
import {
  atom,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXRef,
  useStateXRefValue,
} from '@cloudio-saas/statex';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import React, { Suspense } from 'react';
import ReactTimeago from 'react-timeago';
import useHotkey from '../components/Button/useHotKey';
import {
  useCurrentDataStore,
  useCurrentRecord,
  useStoreRecords,
} from '../components/DataStoreReference/DataStoreReference';
import { UserProfilePic } from '../components/UserPic/UserPic';
import Icon from '../icons/Icon';
import Store, { QueryProps } from '../store/Store';
import useDataStore from '../store/useDataStore';
import { getIoAuditStoreProps } from '../store/utils/utils';
import { isNull } from '../utils/isEmpty';
import SimpleLoading from './SimpleLoading';
import { useUserDateTimeFormat } from './useUser';

export const storeRefPath = ['transient', 'auditStoreRef'];

export interface Props {
  appUid: string;
  datasourceUid: string;
  pkValue: string;
  viewAttribute?: string;
}

export const auditAtom = atom<Props | null>({
  defaultValue: null,
  path: ['transient', 'audit'],
});

const auditFilter: (
  datasourceUid: string,
  pkValue: string,
  viewAttribute?: string,
) => QueryProps<IoAudit> = (datasourceUid, pkValue, viewAttribute) => {
  const query: QueryProps<IoAudit> = {
    filter: [],
    data: { datasourceUid, pkValue },
    sort: { auditId: -1 },
    limit: 2000,
    force: true,
  };

  if (query.data && viewAttribute) {
    query.data.attributeCode = viewAttribute;
  }

  return query;
};

function AuditRow({
  row,
  store,
  viewAttribute,
}: {
  store: Store<DataRecord>;
  row: DBRow<IoAudit>;
  viewAttribute?: string;
}): React.ReactElement {
  let oldValue: string | undefined;
  let newValue: string | undefined;
  const dateTimeFormat = useUserDateTimeFormat();

  switch (row.valueType) {
    case 'String':
      oldValue = row.oldStringValue;
      newValue = row.newStringValue;
      break;
    case 'Clob':
      oldValue = row.oldClobValue;
      newValue = row.newClobValue;
      break;
    case 'DateTime':
      let val = row.oldDatetimeValue;
      if (!isNull(val)) {
        oldValue = format(parseISO(val), dateTimeFormat);
      }
      val = row.newDatetimeValue;
      if (!isNull(val)) {
        newValue = format(parseISO(val), dateTimeFormat);
      }
      break;
    case 'Double':
      if (!isNull(row.oldDoubleValue)) {
        oldValue = String(row.oldDoubleValue);
      }
      if (!isNull(row.newDoubleValue)) {
        newValue = String(row.newDoubleValue);
      }
      break;
  }
  const attr = store.getAttribute(row.attributeCode);
  if (!isNull(newValue) && isNull(oldValue)) {
    return (
      <Typography component="div">
        <UserProfilePic userName={row.updatedBy} size={24} /> set
        {viewAttribute
          ? ' '
          : ` [${attr?.attributeName ?? row.attributeCode}] `}
        to [{newValue}] <ReactTimeago date={row.updateDate} />
      </Typography>
    );
  }
  if (!isNull(oldValue) && isNull(newValue)) {
    return (
      <Typography component="div">
        <UserProfilePic userName={row.updatedBy} size={24} /> cleared
        {viewAttribute
          ? ' '
          : ` [${attr?.attributeName ?? row.attributeCode}] `}
        with value [{oldValue}] <ReactTimeago date={row.updateDate} />
      </Typography>
    );
  }
  return (
    <Typography component="div">
      <UserProfilePic userName={row.updatedBy} size={24} showUserName /> updated
      {viewAttribute ? ' ' : ` [${attr?.attributeName ?? row.attributeCode}] `}
      from [{oldValue}] to [{newValue}] <ReactTimeago date={row.updateDate} />
    </Typography>
  );
}

function Audit({
  datasourceUid,
  appUid,
  pkValue,
  viewAttribute,
  store,
}: Props & { store: Store<DataRecord> }): React.ReactElement {
  const auditStore = useDataStore<IoAudit>(getIoAuditStoreProps(appUid));
  auditStore.read({
    props: auditFilter(datasourceUid, pkValue, viewAttribute),
  });

  const records = useStoreRecords(auditStore) as DBRow<IoAudit>[];

  if (records.length === 0) {
    return <Typography>No changes made!</Typography>;
  }
  return (
    <>
      {records.map((row) => (
        <AuditRow
          row={row}
          key={row.auditId}
          store={store}
          viewAttribute={viewAttribute}
        />
      ))}
    </>
  );
}

export function AuditWrapper() {
  const [props, setProps] = useStateXAtom(auditAtom);
  const ref = useStateXRef<Store<DataRecord> | null>(storeRefPath, null);

  if (props && !ref?.current) {
    console.warn('missing store!');
  }

  if (!(props && ref?.current)) return null;

  function onClose() {
    setProps(null);
  }

  const field = props.viewAttribute
    ? ref.current.getAttribute(props.viewAttribute)?.attributeName ??
      props.viewAttribute
    : '';

  return (
    <Dialog onClose={onClose} open fullScreen>
      <DialogTitle>
        Audit{field ? ` for field [${field}]` : ''}
        {field ? (
          <Button
            onClick={() => {
              setProps({ ...props, viewAttribute: undefined });
            }}
          >
            View audit for all fields
          </Button>
        ) : null}
        <IconButton
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 4, pt: 0, userSelect: 'text' }}>
        <Suspense fallback={<SimpleLoading name="audit" />}>
          <Audit {...props} store={ref.current} />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

export function useAudit({
  appUid,
  pageId,
  active,
  viewAttribute,
}: {
  appUid: string;
  pageId: string;
  active: boolean;
  viewAttribute?: string;
}) {
  const store = useCurrentDataStore<DataRecord>();
  const record = useCurrentRecord<DataRecord>();
  const setAuditProps = useStateXAtomSetter(auditAtom);
  const ref = useStateXRefValue(storeRefPath);

  useHotkey({
    hotKey: 'u',
    handleHotKey: async () => {
      if (!(store && record && active)) return;
      const pks = await store.getPrimaryKeyAttributes();
      const pkValues: string[] = [];
      pks.forEach((attr) => {
        pkValues.push(String(record[attr.attributeCode]));
      });
      const pkValue = pkValues.join(':');
      const ds = store.ds;
      if (!(store && record && ref)) return;
      ref.current = store;
      setAuditProps({
        pkValue,
        datasourceUid: ds,
        appUid,
        viewAttribute,
      });
    },
    appUid,
    pageId,
    filter: () => active && !!store && !!record,
  });
}
